import {
  type DayStatus,
  type DeviceKind,
  type PunchFlag,
  type PunchMode,
  type TodayState,
  formatDate,
} from '@thrivehrm/shared';

/** "16 Sep 2026" or "16 Sep 2026 – 18 Sep 2026". */
export function formatWfhSpan(fromDate: string, toDate: string): string {
  return fromDate === toDate
    ? formatDate(fromDate)
    : `${formatDate(fromDate)} – ${formatDate(toDate)}`;
}

/** "1 working day", "3 working days". */
export const formatWorkingDays = (n: number) => `${String(n)} working day${n === 1 ? '' : 's'}`;

/** Office / WFH as the today card shows them. */
export const PUNCH_MODE_LABELS: Readonly<Record<PunchMode, string>> = {
  OFFICE: 'Office',
  WFH: 'WFH',
};

/** Why HR will review a punch (FR-ATT-4). */
export const PUNCH_FLAG_LABELS: Readonly<Record<PunchFlag, string>> = {
  OUTSIDE: 'Outside office',
  NO_LOCATION: 'No location',
  WFH_NOT_APPROVED: 'WFH not approved',
};

/** How each adapter is named in the UI (FR-ATT-5, ADR-082). */
export const DEVICE_KIND_LABELS: Readonly<Record<DeviceKind, string>> = {
  ZKTECO_ADMS: 'ZKTeco (ADMS push)',
  GENERIC_PUSH: 'Generic push (secret)',
};

export const deviceKindLabel = (kind: string) =>
  kind in DEVICE_KIND_LABELS ? DEVICE_KIND_LABELS[kind as DeviceKind] : kind;

export type DayStatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/** Day statuses (FR-ATT-8): label, a short code for grids, and a status tone. */
export const DAY_STATUS_META: Readonly<
  Record<DayStatus, { label: string; short: string; tone: DayStatusTone }>
> = {
  PRESENT: { label: 'Present', short: 'P', tone: 'success' },
  LATE: { label: 'Late', short: 'L', tone: 'warning' },
  HALF_DAY: { label: 'Half day', short: 'H', tone: 'warning' },
  INCOMPLETE: { label: 'Incomplete', short: 'I', tone: 'warning' },
  ABSENT: { label: 'Absent', short: 'A', tone: 'danger' },
  ON_LEAVE: { label: 'On leave', short: 'LV', tone: 'info' },
  HALF_LEAVE: { label: 'Half leave', short: 'HL', tone: 'info' },
  WFH: { label: 'WFH', short: 'W', tone: 'info' },
  HOLIDAY: { label: 'Holiday', short: 'HO', tone: 'neutral' },
  WEEKEND: { label: 'Weekend', short: '–', tone: 'neutral' },
};

/** Where someone is today (team board). */
export const TODAY_STATE_LABELS: Readonly<Record<TodayState, string>> = {
  IN: 'In',
  LEFT: 'Left',
  WFH: 'WFH',
  ON_LEAVE: 'On leave',
  HALF_LEAVE: 'Half leave',
  NOT_CHECKED_IN: 'Not checked in',
  OFF: 'Day off',
};

/** `540` → `9 h 00 m`; 0 → `—`. */
export const formatWorked = (minutes: number) =>
  minutes <= 0
    ? '—'
    : `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')} m`;
